import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, HostBinding } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NavController, ToastController } from '@ionic/angular';
import { Events } from 'src/providers/events/events';

import { RoutingState } from 'src/shared/providers/providers/routing-state';
import { AuthModalService } from 'src/shared/services/auth-modal.service';

import { DateTime as LuxonDateTime } from 'luxon';
import { Capacitor } from '@capacitor/core';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Subscription } from 'rxjs';
import { Auth } from 'src/providers/auth/auth';
import { NotificationsQueriesService } from 'src/shared/services/notifications-queries.service';

@Component({
    selector: 'tidy-top-nav',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./top-nav.component.scss'],
    templateUrl: './top-nav.component.html',
    standalone: false
})

export class TopNavComponent implements OnInit, OnDestroy {

  @Input() backButton: boolean;
  @Input() canGoBack: boolean;
  @Input() countdownTarget: LuxonDateTime;
  @Input() customBack: string | null;
  @Input() closeIconMobile = 'close-circle-white';
  @Input() closeIconDesktop = 'close-circle';
  @Input() linkIcon: string;
  @Input() linkUrl: string;
  @Input() noBow = false;
  @Input() showFullLogo = false;
  @Input() navigationExtras = {};
  @Input() title: string;
  @Input() subtitle: string;
  @Input() subtitleIcon: string;
  @Input() inlineSubtitle = false;
  @Input() showLinkIcon = false;
  @Input() disabledLinkAction = false;
  @Input() showDropdownMenu = false;
  @Input() dropdownMenu: { label: string; action: string }[] = [];
  @Input() showLoadingSpinner = false;
  @Input() closeIconSrc: string;
  @Input() linkIconSrc: string;
  @Input() linkIconSize = 22;
  @Input() customBackAction: () => boolean;
  @Output() closeItemAction: EventEmitter<any> = new EventEmitter();
  @Output() linkAction: EventEmitter<any> = new EventEmitter();
  @Output() backAction: EventEmitter<void> = new EventEmitter();
  private refreshNotificationCountSubscription: Subscription;
  @Input() secondLinkIcon: string;
  @Input() showSecondLinkIcon = false;
  @Output() secondLinkAction: EventEmitter<any> = new EventEmitter();
  @Input() showAgentIcon = false;
  @Input() forceShowAgentIcon = false;
  @Output() agentAction: EventEmitter<any> = new EventEmitter();
  loading = false;
  checkGoBack;
  current: string;
  logo: string;
  fullLogo = '/assets/img/tidy-logo.svg';
  isRightSidePanelOpen = false;
  isRightSidePanelOpenSubscription$: Subscription;
  backgroundColor: string;

  @HostBinding('class.hidden') get isHidden() {
    return this.isRightSidePanelOpen;
  }

  @HostBinding('style.background-color') get bgColor() {
    return this.backgroundColor;
  }


  constructor(
    private authModalService: AuthModalService,
    private customNavController: NavController,
    private router: Router,
    private routingState: RoutingState,
    private toastCtrl: ToastController,
    private events: Events,
    public onboardingProvider: OnboardingProvider,
    public windowService: WindowService,
    private notificationsQueries: NotificationsQueriesService,
    private rightSidePanelService: RightSidePanelService,
    private auth: Auth
  ) {
    this.events.subscribe('updateMenu', () => this.loadLogo());
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkGoBack = this.routingState.canGoBack();
        this.current = event.url;
      }
    });
    if (this.windowService.isDesktopRes) {
      this.backgroundColor = '';
    } else {
      this.backgroundColor = '#1D2125';
    }
  }

  ngOnInit() {
    // Layout sizing must run even when logged out — public pages like the
    // password-recovery screen render the top nav, and without these CSS vars
    // the header overlaps the status bar and the content jams to the top.
    this.setTopNavSpaceSize();
    if (!this.auth.isAuthenticated()) {
      return;
    }
    this.loadLogo();
    this.watchRightSidePanelOpen();
  }

  get notificationCount(): number {
    if (this.linkIcon !== 'notification') {
      return 0;
    }
    return this.notificationsQueries.notificationCount();
  }

  ngOnDestroy() {
    document.documentElement.style.setProperty('--top-nav-total-height', '0px');
    if (this.isRightSidePanelOpenSubscription$) {
      this.isRightSidePanelOpenSubscription$?.unsubscribe();
    }
  }

  watchRightSidePanelOpen() {
    this.isRightSidePanelOpenSubscription$ = this.rightSidePanelService.isOpen$.subscribe(isOpen => {
      this.isRightSidePanelOpen = isOpen;

    });
  }

  private setTopNavSpaceSize() {
    let spaceSize = '0px 20px 13px 20px';
    let totalHeight = '0px';
    if (!this.windowService.isDesktopRes) {
      spaceSize = '13px 20px 13px 20px';
      totalHeight = '55px';
    }
    if (Capacitor.getPlatform() === 'android') {
      spaceSize = '47px 20px 13px 20px';
      totalHeight = '89px';
    }
    if (Capacitor.getPlatform() === 'ios') {
      spaceSize = '60px 20px 13px 20px';
      totalHeight = '102px';
    }
    document.documentElement.style.setProperty('--top-nav-space-size', spaceSize);
    document.documentElement.style.setProperty('--top-nav-total-height', totalHeight);
  }


  navigateToNotificationCenter() {
    this.customNavController.navigateForward('notification-center');
  }

  dropDownMenuClicked(action: string): void {
    this.rightSidePanelService.emitDropDownMenuAction(action);
  }

  loadLogo(){
    this.logo = "/assets/img/bowtie-white.svg";
  }

  showCloseItem() {
    return (this.closeItemAction.observers.length > 0);
  }

  async executeAction(emitter: EventEmitter<any>) {
    if (this.disabledLinkAction) {
      return;
    }
    this.loading = true;
    try {
      await emitter.emit();
    } catch (error) {
      this.showError(error);
    }
    this.loading = false;
  }

  goBack() {
    if (this.backAction.observed) {
      this.backAction.emit();
    }
    let shouldNavigate = true;
    if (this.customBackAction && typeof this.customBackAction === 'function') {
      const result = this.customBackAction();
      if (result === false) {
        shouldNavigate = false;
      }
    }
    if (shouldNavigate) {
      if (this.customBack) {
        this.customNavController.navigateForward(this.customBack, this.navigationExtras);
      } else {
        this.customNavController.back();
      }
    }
  }

  async showError(message) {
    const toast = await this.toastCtrl.create({ message, duration: 3000 });
    toast.present();
  }

}